<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";

const sections = [
  {
    title: "Practice MCQs",
    to: "/11th/computer/practice/mcqs/",
    description: "All chapters ke MCQs ko filters aur random mode ke sath practice karo.",
  },
  {
    title: "Practice Short Questions",
    to: "/11th/computer/practice/short-questions/",
    description: "Flashcard-style short questions ko hide/show answers mode me revise karo.",
  },
  {
    title: "Practice Long Questions",
    to: "/11th/computer/practice/long-questions/",
    description: "Structured answers ke sath exam-prep long questions ko unit-wise kholo.",
  },
];

const useCases = [
  "Aap ko mixed MCQ revision chahiye ho",
  "Aap answers hide karke self-check karna chahte ho",
  "Aap ko guess-paper style long answers revise karne hon",
];

const relatedLinks = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/", description: "Pehle samjhein kis chapter se kya aata hai." },
  { title: "MCQs Hub", to: "/11th/computer/mcqs/", description: "Chapter-wise objective prep ke liye." },
  { title: "Important Questions", to: "/11th/computer/important-questions/", description: "High-priority sawalat ke liye." },
];

const faqs = [
  {
    question: "11th class computer practice mode kis liye useful hai?",
    answer:
      "Practice mode mixed revision ke liye useful hai. Is me aap MCQs, short questions aur long questions ko exam-style flow me revise kar sakte hain.",
  },
  {
    question: "Practice mode aur chapter pages me kya difference hai?",
    answer:
      "Chapter pages topic-wise prep ke liye hote hain, jab ke practice mode mixed self-check aur fast revision ke liye hota hai.",
  },
  {
    question: "1st year computer ke liye pehle practice mode kholo ya chapter page?",
    answer:
      "Agar concept weak ho to chapter page kholo. Agar revision ya test mode chahiye ho to practice mode se start karna better hota hai.",
  },
];

usePageSeo({
  title: "11th Class Computer Practice Mode | Ustaad",
  description: "11th class computer practice mode hub: mixed MCQs, flashcard short questions aur long-question practice filters ke sath.",
  keywords: "11th class computer practice, first year computer online practice, computer mcqs practice, short questions practice, long questions practice",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Practice Mode", item: "/11th/computer/practice/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Practice Mode",
    items: sections.map((section) => ({ name: section.title, path: section.to })),
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Practice Mode</p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">11th Class Computer Practice</h1>
      <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
        Yeh mixed practice hub hai. Chapter pages se alag, yahan aap MCQs, short questions aur long questions ko combined practice mode me kar sakte hain.
      </p>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
        Revision ke stage par yeh page useful hota hai, kyun ke yahan student ko direct self-check modes mil jate hain.
      </p>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Practice Modes</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Choose how you want to practice</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="section in sections"
          :key="section.to"
          :to="section.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ section.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ section.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Best Use</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">When this helps most</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <article
          v-for="item in useCases"
          :key="item"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <p class="text-sm leading-7 text-slate-700 dark:text-slate-300">{{ item }}</p>
        </article>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Prep</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Use practice mode with core pages</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in relatedLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Practice FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
